/**
 * Schedule Page — Общее расписание занятий
 *
 * Сводная таблица занятий по всем центрам на неделю.
 * Группировка по дням, фильтры в заголовках столбцов (Excel-style).
 * Навигация по неделям. Клик на строку → модалка редактирования.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title, Text, Stack, Box, Group, Loader, Center, Badge,
  Table, ActionIcon, Tooltip, Popover,
} from '@mantine/core';
import {
  IconChevronLeft, IconChevronRight, IconPoint, IconFilter,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import LessonEditModal from '../components/Organizations/widgets/LessonEditModal';
import ColumnFilterPopover from '../components/common/ColumnFilterPopover';
import { getGlobalSchedule, getGlobalScheduleFilterOptions } from '../services/api/lessonsService';
import tokens from '../theme/designTokens';
import { useSession } from '../context/SessionContext';
import { useSessionState } from '../hooks/useSessionState';

// Конфиги статусов и типов (те же что в ScheduleWidget)
const STATUS_CONFIG = {
  active: { label: 'В силе', color: 'green' },
  cancelled: { label: 'Отмена', color: 'red' },
  substitute: { label: 'Замена', color: 'orange' },
  rescheduled: { label: 'Перенос', color: 'blue' },
  was_rescheduled: { label: 'Из переноса', color: 'cyan' },
};

const TYPE_CONFIG = {
  lesson: { label: 'ЗН', color: 'gray' },
  master_class: { label: 'МК', color: 'violet' },
  offline: { label: 'offline', color: 'teal' },
  online: { label: 'online', color: 'indigo' },
  group_online: { label: 'гр. онлайн', color: 'grape' },
};

// Утилиты дат
function getMonday(d = new Date()) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split('T')[0];
}

function formatDateFull(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
}

function formatDateShort(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Moscow',
  });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  return timeStr.slice(0, 5);
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

// Конвертировать Set фильтра в массив для API (убираем __none__)
function setToApiArray(filterSet) {
  return [...filterSet].filter((v) => v !== '__none__');
}

// Группировка занятий по временному слоту (start_time + end_time)
// Данные уже отсортированы бэкендом по start_time, organization_id
function groupByTimeSlot(lessons) {
  const groups = [];
  let current = null;
  for (const lesson of lessons) {
    const key = `${lesson.start_time}-${lesson.end_time}`;
    if (!current || current.key !== key) {
      current = { key, lessons: [] };
      groups.push(current);
    }
    current.lessons.push(lesson);
  }
  return groups;
}

/**
 * FilterTh — заголовок столбца с Excel-style фильтром
 */
const FilterTh = ({ label, column, filter, onChange }) => {
  const isActive = filter.size > 0;

  return (
    <Table.Th>
      <Group gap={4} wrap="nowrap" align="center">
        <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
          {label}
        </Text>
        <Popover
          position="bottom-start"
          withArrow
          shadow="md"
          withinPortal
          trapFocus
        >
          <Popover.Target>
            <ActionIcon
              size="xs"
              variant={isActive ? 'filled' : 'subtle'}
              color={isActive ? 'blue' : 'gray'}
            >
              <IconFilter size={12} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <ColumnFilterPopover
              column={column}
              data={[]}
              filter={filter}
              onChange={onChange}
              onClose={() => {}}
            />
          </Popover.Dropdown>
        </Popover>
      </Group>
    </Table.Th>
  );
};

const Schedule = () => {
  const { user } = useSession();
  const canEdit = ['ADMIN', 'MANAGER'].includes(user?.role);

  // Навигация по неделям
  const [weekStart, setWeekStart] = useSessionState('schedule:weekStart', getMonday());

  // Фильтры — Set-based (пустой Set = показать всё)
  const [filterOrgIds, setFilterOrgIds] = useState(new Set());
  const [filterStatuses, setFilterStatuses] = useState(new Set());
  const [filterTypes, setFilterTypes] = useState(new Set());
  const [filterTeacherIds, setFilterTeacherIds] = useState(new Set());

  // Опции фильтров (загружаются однажды)
  const [filterOptions, setFilterOptions] = useState(null);

  // Данные расписания
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Модалка редактирования
  const [editLesson, setEditLesson] = useState(null);

  // Загрузить опции для фильтров
  useEffect(() => {
    getGlobalScheduleFilterOptions()
      .then(setFilterOptions)
      .catch((err) => console.error('Ошибка загрузки опций фильтров:', err));
  }, []);

  // Проверка: есть ли фильтр с «ничего не выбрано» (__none__)
  const hasNoneFilter = filterOrgIds.has('__none__')
    || filterStatuses.has('__none__')
    || filterTypes.has('__none__')
    || filterTeacherIds.has('__none__');

  // Загрузить расписание при изменении недели или фильтров
  const loadSchedule = useCallback(async () => {
    // Если один из фильтров = «ничего не выбрано», показываем пустой результат
    if (hasNoneFilter) {
      setData({ week_start: weekStart, week_end: addDays(weekStart, 6), days: [], summary: { total: 0 } });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const filters = {
        organization_ids: setToApiArray(filterOrgIds),
        statuses: setToApiArray(filterStatuses),
        lesson_types: setToApiArray(filterTypes),
        teacher_ids: setToApiArray(filterTeacherIds),
      };
      const result = await getGlobalSchedule(weekStart, filters);
      setData(result);
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [weekStart, filterOrgIds, filterStatuses, filterTypes, filterTeacherIds, hasNoneFilter]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  // Навигация
  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const goToCurrentWeek = () => setWeekStart(getMonday());

  const handleLessonSaved = async () => {
    setEditLesson(null);
    await loadSchedule();
  };

  // Диапазон текущей недели
  const weekEnd = data?.week_end || addDays(weekStart, 6);
  const weekRange = `${formatDateShort(weekStart)} — ${formatDateFull(weekEnd)}`;

  const totalLessons = data?.summary?.total || 0;
  const hasLessons = data?.days?.some((d) => d.lessons.length > 0);

  // Опции для ColumnFilterPopover
  const orgFilterOptions = (filterOptions?.organizations || []).map((o) => ({
    value: String(o.id),
    label: o.name,
  }));
  const teacherFilterOptions = (filterOptions?.teachers || []).map((t) => ({
    value: String(t.id),
    label: t.name,
  }));
  const statusFilterOptions = [
    { value: 'active', label: 'В силе' },
    { value: 'cancelled', label: 'Отмена' },
    { value: 'substitute', label: 'Замена' },
    { value: 'rescheduled', label: 'Перенос' },
    { value: 'was_rescheduled', label: 'Из переноса' },
  ];
  const typeFilterOptions = [
    { value: 'lesson', label: 'ЗН (занятие)' },
    { value: 'master_class', label: 'МК (мастер-класс)' },
    { value: 'offline', label: 'Оффлайн' },
    { value: 'online', label: 'Онлайн' },
    { value: 'group_online', label: 'Группа онлайн' },
  ];

  // Конфиги колонок для FilterTh
  const columnOrg = { key: 'organization_id', label: 'Центр', filterOptions: orgFilterOptions };
  const columnStatus = { key: 'status', label: 'Статус', filterOptions: statusFilterOptions };
  const columnType = { key: 'lesson_type', label: 'Тип', filterOptions: typeFilterOptions };
  const columnTeacher = { key: 'teacher_user_id', label: 'Педагог', filterOptions: teacherFilterOptions };

  return (
    <>
      <Box px="md" py="md">
        <Stack gap="md">
          {/* Заголовок */}
          <Group justify="space-between" align="flex-end">
            <Box>
              <Title order={2} style={{ color: tokens.colors.brand.primary }}>
                Общее расписание
              </Title>
              <Text c="dimmed" size="sm">
                {totalLessons > 0
                  ? `${totalLessons} занятий за неделю`
                  : 'Занятия на этой неделе не найдены'}
              </Text>
            </Box>
          </Group>

          {/* Навигация по неделям */}
          <Group gap="sm" align="center">
            <Tooltip label="Предыдущая неделя">
              <ActionIcon variant="subtle" size="md" onClick={prevWeek}>
                <IconChevronLeft size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Текущая неделя">
              <ActionIcon variant="subtle" size="md" onClick={goToCurrentWeek}>
                <IconPoint size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Следующая неделя">
              <ActionIcon variant="subtle" size="md" onClick={nextWeek}>
                <IconChevronRight size={18} />
              </ActionIcon>
            </Tooltip>
            <Text size="sm" fw={500} c={tokens.colors.neutral[8]}>
              {weekRange}
            </Text>
          </Group>

          {/* Таблица — заголовки с фильтрами всегда видны */}
          <Table
            striped
            highlightOnHover
            withTableBorder={false}
            style={{ fontSize: tokens.typography.fontSize[13] }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Время</Table.Th>
                <FilterTh
                  label="Центр"
                  column={columnOrg}
                  filter={filterOrgIds}
                  onChange={setFilterOrgIds}
                />
                <Table.Th>Адрес</Table.Th>
                <Table.Th>Менеджер</Table.Th>
                <Table.Th>Группа</Table.Th>
                <FilterTh
                  label="Тип"
                  column={columnType}
                  filter={filterTypes}
                  onChange={setFilterTypes}
                />
                <FilterTh
                  label="Статус"
                  column={columnStatus}
                  filter={filterStatuses}
                  onChange={setFilterStatuses}
                />
                <Table.Th>Замена</Table.Th>
                <FilterTh
                  label="Педагог"
                  column={columnTeacher}
                  filter={filterTeacherIds}
                  onChange={setFilterTeacherIds}
                />
                <Table.Th>Предмет</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr style={{ pointerEvents: 'none' }}>
                  <Table.Td colSpan={10}>
                    <Center py="xl">
                      <Loader size="md" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : !hasLessons ? (
                <Table.Tr style={{ pointerEvents: 'none' }}>
                  <Table.Td colSpan={10}>
                    <Center py="xl">
                      <Text c="dimmed">На этой неделе нет занятий</Text>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (
                data.days.map((day) => {
                  if (day.lessons.length === 0) return null;
                  return (
                    <React.Fragment key={day.date}>
                      {/* Строка-разделитель с названием дня */}
                      <Table.Tr style={{ pointerEvents: 'none' }}>
                        <Table.Td
                          colSpan={10}
                          style={{
                            backgroundColor: 'white',
                            paddingTop: 20,
                            paddingBottom: 6,
                            borderBottom: `2px solid ${tokens.colors.brand.primary}`,
                          }}
                        >
                          <Text
                            fw={600}
                            size="sm"
                            style={{ color: tokens.colors.brand.primary }}
                          >
                            {day.day_name}, {formatDateFull(day.date)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>

                      {/* Строки занятий за этот день — сгруппированы по времени */}
                      {groupByTimeSlot(day.lessons).map((group) =>
                        group.lessons.map((lesson, idx) => {
                          const statusCfg = STATUS_CONFIG[lesson.status] || STATUS_CONFIG.active;
                          const typeCfg = TYPE_CONFIG[lesson.lesson_type] || TYPE_CONFIG.lesson;

                          return (
                            <Table.Tr
                              key={lesson.id}
                              style={{ cursor: canEdit ? 'pointer' : 'default' }}
                              onClick={() => canEdit && setEditLesson(lesson)}
                            >
                              {/* Время — rowSpan для первой строки группы */}
                              {idx === 0 && (
                                <Table.Td
                                  rowSpan={group.lessons.length}
                                  style={{
                                    verticalAlign: 'middle',
                                    borderRight: group.lessons.length > 1
                                      ? `1px solid ${tokens.colors.neutral[3]}`
                                      : undefined,
                                  }}
                                >
                                  <Text size="sm" fw={500}>
                                    {formatTime(lesson.start_time)}–{formatTime(lesson.end_time)}
                                  </Text>
                                </Table.Td>
                              )}
                              <Table.Td>
                                <Text size="sm" fw={500}>
                                  {lesson.organization_name || '—'}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {lesson.organization_address || '—'}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" c={lesson.manager_name ? undefined : 'dimmed'}>
                                  {lesson.manager_name || '—'}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{lesson.group_name}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge size="xs" variant="filled" color={typeCfg.color} radius="xl">
                                  {typeCfg.label}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge size="xs" variant="filled" color={statusCfg.color} radius="xl">
                                  {statusCfg.label}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                {lesson.substitute_searching ? (
                                  <Badge size="xs" variant="filled" color="red" radius="xl">
                                    В поиске
                                  </Badge>
                                ) : lesson.substitute_teacher_name ? (
                                  <Text size="sm">{lesson.substitute_teacher_name}</Text>
                                ) : (
                                  <Text size="sm" c="dimmed">Нет</Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{lesson.teacher_name || '—'}</Text>
                              </Table.Td>
                              <Table.Td>
                                {lesson.subject_name ? (
                                  <Badge size="xs" variant="filled" color="blue" radius="xl">
                                    {lesson.subject_name}
                                  </Badge>
                                ) : (
                                  <Text size="sm" c="dimmed">—</Text>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          );
                        })
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Stack>
      </Box>

      {/* Модалка редактирования занятия */}
      {editLesson && (
        <LessonEditModal
          opened={!!editLesson}
          onClose={() => setEditLesson(null)}
          lesson={editLesson}
          organizationId={editLesson.organization_id}
          onSaved={handleLessonSaved}
        />
      )}
    </>
  );
};

export default Schedule;
